import { FileText, ShieldAlert, CheckCircle2, RotateCcw } from 'lucide-react';
import { usePageSEO } from '../hooks/usePageSEO';

export function TermsOfService() {
  usePageSEO({
    title: 'Terms of Use & Herbal Product Disclaimer | WellZora Herbal Care',
    description: 'Read the official terms of use, product disclaimer, and return/exchange policy for WellZora Herbal Care natural herbal remedies.',
    canonical: 'https://wellzoraherbalcare.com/terms',
  });

  return (
    <div className="min-h-screen bg-gray-50 py-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 sm:p-12">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 bg-emerald-50 text-primary rounded-full text-xs font-semibold uppercase tracking-wider mb-4">
            <FileText className="w-3.5 h-3.5" /> Terms &amp; Compliance
          </div>

          <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900 tracking-tight mb-4">
            Terms of Use &amp; Medical Disclaimer
          </h1>
          <p className="text-gray-500 text-sm mb-8 pb-6 border-b border-gray-100">
            Last Updated: September 2026 • WellZora Herbal Care Pakistan
          </p>

          <div className="space-y-8 text-gray-700 leading-relaxed">
            {/* Medical Disclaimer Alert Box */}
            <div className="bg-amber-50/80 border border-amber-200 rounded-xl p-5 text-amber-900">
              <div className="flex items-start gap-3">
                <ShieldAlert className="w-6 h-6 text-amber-700 flex-shrink-0 mt-0.5" />
                <div>
                  <h2 className="text-base font-bold text-amber-900 mb-1">Important Herbal Supplement Disclaimer</h2>
                  <p className="text-xs sm:text-sm text-amber-800 leading-relaxed">
                    WellZora Herbal Care formulations are traditional, 100% natural herbal dietary preparations designed under botanical and Unani principles. Information presented on this website, product packaging, and via WhatsApp consultations is strictly for educational, nutritional, and general wellness purposes. It is not intended as a substitute for clinical medical diagnosis, prescription medications, or personalized treatment from a licensed medical doctor.
                  </p>
                </div>
              </div>
            </div>

            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-primary" /> 1. Quality &amp; Natural Composition
              </h2>
              <p>
                All products prepared under the WellZora Herbal Care label are formulated using authentic botanical herbs, natural honey, dry fruits, and laboratory-tested traditional ingredients. We strictly guarantee that our preparations are 100% free from synthetic steroids, harmful chemical stimulants, or banned substances.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                <RotateCcw className="w-5 h-5 text-primary" /> 2. Returns, Replacements &amp; Exchanges
              </h2>
              <p className="mb-2">
                Customer satisfaction and health are our highest priorities:
              </p>
              <ul className="list-disc pl-5 space-y-1.5 text-sm text-gray-600">
                <li>
                  <strong>Damaged or Incorrect Items:</strong> If you receive an incorrect product or a damaged seal, notify our support within 48 hours of delivery for a free immediate replacement.
                </li>
                <li>
                  <strong>Hygiene &amp; Safety:</strong> Due to health, food-safety, and hygiene standards, unsealed or partially consumed herbal majoon, safoof, or oils cannot be returned once opened, unless verified as defective upon arrival.
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3">3. Individual Results</h2>
              <p>
                As with all natural and holistic herbal treatments, individual physiological responses, metabolism, and timelines may vary. Recommended usage instructions provided in our product leaflets should be consistently followed alongside a balanced diet and healthy lifestyle.
              </p>
            </section>

            <section className="pt-6 border-t border-gray-100">
              <h2 className="text-lg font-bold text-gray-900 mb-2">Customer Helpline</h2>
              <p className="text-sm text-gray-600">
                For questions regarding product ingredients, usage guidelines, or order status, reach out directly to our customer care team at <strong>0305-6367673</strong> or <a href="mailto:wellzoraherbalcare@gmail.com" className="text-primary font-medium hover:underline">wellzoraherbalcare@gmail.com</a>.
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}
